import React, { memo, useRef, useState } from 'react';
import Select from 'react-select';
import { MultiSelect } from '../MultiSelect';
import StrictOrderUserNotificationSettings from '../strict-order-user-notification-settings';
import {
  FORM_FOLDER_KEY,
  getStepName,
  HIRE_DATE_FILTER_KEY,
  getStepSubmitterOptions,
  getStepSubmitters,
  HIRE_MONTH_KEY,
  HIRE_MONTH_OPTIONS,
  isOfHireMonth,
  isUserGroupSubmitter,
  MIN_STRICT_ORDER_STEPS,
  ONE_PER_GROUP_KEY,
  STEP_NAME_KEY,
  STEP_NAME_MAX_LENGTH,
} from '../../../utility/forms/strict-order';
import './styles.scss';

// The options a creator's step of a multi-submission form can have, shown next to its submitters.
const STEP_OPTIONS = [
  { key: FORM_FOLDER_KEY, label: 'Create a folder for a form in “Forms Assigned to Me”' },
  { key: ONE_PER_GROUP_KEY, label: 'Only one submission is required from each group member' },
  { key: HIRE_DATE_FILTER_KEY, label: 'Show the Hire Date' },
];

// With `splitByEntityType` a step has a picker of each kind of submitter, user groups first.
const SUBMITTER_KINDS = [
  {
    key: 'user_groups',
    label: 'User groups',
    placeholder: 'Choose one or several user groups',
    allName: 'All user groups',
    isKind: isUserGroupSubmitter,
  },
  {
    key: 'individual_users',
    label: 'Individual users',
    placeholder: 'Choose one or several individual users',
    allName: 'All individual users',
    isKind: (submitter) => !isUserGroupSubmitter(submitter),
  },
];

/**
 * A step's "Hire date": the month its people were hired in, of an earlier year. Opened
 * from the step's button with no month yet, it comes up with its months listed.
 */
const StepHireMonth = ({ hireMonth, onChange, onRemove }) => {
  const month = HIRE_MONTH_OPTIONS.find((option) => option.value === hireMonth);

  return (
    <div className="strict-order-step-hire-month">
      <label className="form-label">Hire date</label>
      <div className="d-flex mb-0">
        <Select
          classNamePrefix="react-select"
          className="form-control-select"
          placeholder="Choose a month"
          options={HIRE_MONTH_OPTIONS}
          value={month ?? null}
          autoFocus={!month}
          openMenuOnFocus
          onChange={(option) => onChange(option.value)}
        />
        <button type="button" className="btn btn-icon btn-remove ml-4" aria-label="Remove the hire date" onClick={onRemove}>
          <i className="icon-remove"></i>
        </button>
      </div>
    </div>
  );
};

/**
 * A step's name, in a row with what goes next to it (`children`) when `hasControls`. With
 * `onRename` a pencil next to it turns it into a text box: Enter or leaving the box keeps
 * what was typed - nothing, or the default "Step N", takes the name away (`onRename(null)`)
 * - and Escape drops it.
 */
const StepName = ({ name, defaultName, onRename, hasControls, children }) => {
  const [draft, setDraft] = useState(null);
  const isCancelled = useRef(false);

  if (draft !== null) {
    const finish = () => {
      if (!isCancelled.current) {
        const typed = draft.trim();
        onRename(typed && typed !== defaultName ? typed : null);
      }
      isCancelled.current = false;
      setDraft(null);
    };

    return (
      <div className="strict-order-step-name">
        <input
          className="form-control strict-order-step-name-input"
          aria-label="Step name"
          autoFocus
          value={draft}
          maxLength={STEP_NAME_MAX_LENGTH}
          placeholder={defaultName}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={finish}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              e.preventDefault();
              isCancelled.current = e.key === 'Escape';
              e.target.blur();
            }
          }}
        />
      </div>
    );
  }

  const label = <label className="form-label form-label-big">{name}</label>;
  if (!onRename && !hasControls) {
    return label;
  }

  return (
    <div className="strict-order-step-name">
      {label}
      {onRename && (
        <button
          type="button"
          className="btn btn-icon strict-order-step-rename-button"
          aria-label={`Rename ${name}`}
          onClick={() => setDraft(name === defaultName ? '' : name)}
        >
          <i className="icon-icon-edit"></i>
        </button>
      )}
      {children}
    </div>
  );
};

/**
 * One picker of a step's submitters: a list of them with `allowMultipleSubmitters`
 * (`value` an array), else a single one (`value` an option or null).
 */
const StepSubmittersPicker = (props) => {
  const { allowMultipleSubmitters, options, value, isFixed, isDisabled, isClearable, placeholder, allName, onChange, removeStepButton } =
    props;

  if (!allowMultipleSubmitters) {
    return (
      <div className={removeStepButton ? 'd-flex mb-0' : ''}>
        <Select
          classNamePrefix="react-select"
          className={`form-control-select`}
          isDisabled={isDisabled}
          isClearable={isClearable}
          options={options}
          value={value}
          onChange={onChange}
        />
        {removeStepButton}
      </div>
    );
  }

  // These settings pages hide react-select's own chips, so the picked submitters are
  // listed under the control and the placeholder is drawn as a static overlay - same
  // as the other multi selects here.
  return (
    <div className="select-multy-wrapper">
      <span className="select-placeholder">{isFixed ? value.map((submitter) => submitter.label).join(', ') : placeholder}</span>
      <div className={removeStepButton ? 'd-flex mb-0' : ''}>
        <MultiSelect isDisabled={isDisabled} options={options} value={value} onChange={onChange} placeholder="" allName={allName} />
        {removeStepButton}
      </div>
      {value.length > 0 && (
        <ul>
          {value.map((submitter, submitterIndex) => (
            // A locked chip has no remove button, which is what spaces the others apart.
            <li key={`${submitter.value}-${submitterIndex}`} style={isFixed ? { marginRight: 24 } : undefined}>
              {submitter.label}
              {!isFixed && (
                <button className="btn btn-icon" onClick={() => onChange(value.filter((_, index) => index !== submitterIndex))}>
                  <img src="/images/x-circle.svg" alt="" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * The ordered list of submitter steps, shared by the form builder and the
 * initiation screen.
 *
 * With `allowMultipleSubmitters` a step holds a list of submitters rather than a
 * single one. Steps after the first still carry `only_one_submission_required`
 * for the API, but it is no longer offered as a checkbox here.
 *
 * With `lockedFinalSubmitters` (options: value/label/entity_type) the last step
 * is those submitters and nothing else: it is drawn locked, and no step can be
 * added after it. The caller keeps that step in `steps`; this only stops it changing.
 *
 * `lockedStepSubmitters` does the same for any step, by index (null = open), and
 * `hideLockedSteps` leaves those steps out altogether - the others keep their "Step N".
 * `isStepCountFixed` hides adding and removing steps, `isNotificationReadOnly`
 * shows every step's "Notify user" without letting it change, `hideNotifications`
 * leaves it out - all for steps the form creator set up (see CREATOR_STEPS_KEY).
 * `allowEmptySteps` lets the creator clear a step's submitter, leaving it for the initiator.
 *
 * `withFormFolders` offers every step "Create a folder for a form in Forms Assigned
 * to Me" (FORM_FOLDER_KEY), and `withOnePerGroup` "Only one submission is required
 * from each group member" (ONE_PER_GROUP_KEY) - the API ignores the latter on the
 * step that sets the number of submissions, where each person fills their own form.
 * `withHireDateOption` offers "Show the Hire Date" (HIRE_DATE_FILTER_KEY): whether the
 * initiator gets the step's "Hire date" button. All are changed through `onChangeStep`
 * and drawn to the right of the step's submitters.
 *
 * `splitByEntityType` gives every step two pickers instead of one, "User groups" and
 * "Individual users", each offering only that kind of `options`. A single-submitter
 * step still holds one of them: picking in one picker takes the place of the other's.
 *
 * `withStepNames` lets every step be renamed (STEP_NAME_KEY, through `onChangeStep`), and
 * `stepNames` names the steps instead of the steps' own STEP_NAME_KEY - else "Step N".
 *
 * `withHireMonth` puts a small "Hire date" button next to every step's name - but the
 * locked final one's; or, as a function of the step's index, next to those it says -
 * showing a month picker that filters the step by HIRE_MONTH_KEY
 * (see isOfHireMonth): it offers, and shows locked, only the users hired in that month
 * of an earlier year and the user groups with such a member (users need `hire_date`,
 * user groups `member_hire_dates`). Picking the month drops the step's others, through
 * `onChangeStepSubmitters(selection, stepIndex, { hire_month })` - the month comes along,
 * to be stored in the same change. Removing it goes through `onChangeStep`.
 */
const StrictOrderSteps = (props) => {
  const {
    steps = [],
    options = [],
    allowMultipleSubmitters = false,
    lockedFinalSubmitters = null,
    lockedStepSubmitters = [],
    hideLockedSteps = false,
    isStepCountFixed = false,
    isNotificationReadOnly = false,
    hideNotifications = false,
    allowEmptySteps = false,
    withFormFolders = false,
    withOnePerGroup = false,
    withHireDateOption = false,
    splitByEntityType = false,
    withHireMonth = false,
    withStepNames = false,
    stepNames = null,
    onChangeStepSubmitters,
    onChangeStep,
    onAddStep,
    onRemoveStep,
    getStepErrors,
    renderStepErrors,
  } = props;

  const renderedSteps = [...steps];
  while (renderedSteps.length < MIN_STRICT_ORDER_STEPS) {
    renderedSteps.push(null);
  }
  const fixedStepIndex = lockedFinalSubmitters ? renderedSteps.length - 1 : -1;
  // Every step keeps the column, even one with nothing in it, so the pickers line up.
  const hasStepOptions = withFormFolders || withOnePerGroup || withHireDateOption;
  // Steps whose "Hire date" button was clicked, with no month picked yet.
  const [hireMonthStepIndexes, setHireMonthStepIndexes] = useState([]);

  return (
    <>
      {renderedSteps.map((step, stepIndex) => {
        if (hideLockedSteps && lockedStepSubmitters?.[stepIndex]) {
          return null;
        }

        const notificationData = {
          should_receive_notification: step?.should_receive_notification,
          notification_type: step?.notification_type,
          notification_text: step?.notification_text,
          errors: getStepErrors ? getStepErrors(stepIndex) : undefined,
        };
        const stepName = stepNames?.[stepIndex] ?? getStepName(step, stepIndex);
        const hasHireMonth = typeof withHireMonth === 'function' ? withHireMonth(stepIndex) : withHireMonth;
        const hireMonth = hasHireMonth ? step?.[HIRE_MONTH_KEY] ?? null : null;
        const canFilter = hasHireMonth && stepIndex !== fixedStepIndex;
        const isHireMonthShown = canFilter && (!!hireMonth || hireMonthStepIndexes.includes(stepIndex));
        const isOfStepHireMonth = (submitter) => isOfHireMonth(submitter, hireMonth);
        const offeredOptions = options.filter(isOfStepHireMonth);
        const lockedSubmitters =
          stepIndex === fixedStepIndex ? lockedFinalSubmitters : lockedStepSubmitters?.[stepIndex]?.filter(isOfStepHireMonth) ?? null;
        const isFixed = !!lockedSubmitters;
        const isRemovable = !isStepCountFixed && stepIndex >= MIN_STRICT_ORDER_STEPS && !isFixed;
        const selectedSubmitters = isFixed ? lockedSubmitters : getStepSubmitterOptions(step, options);
        const selectedSubmitter = isFixed ? lockedSubmitters[0] ?? null : getStepSubmitters(step).length ? step : null;
        // With `splitByEntityType`, the step's submitters of each SUBMITTER_KINDS, resolved among
        // that kind alone: a user and a user group may share an id.
        const selectedByKind = SUBMITTER_KINDS.map((kind) =>
          isFixed
            ? lockedSubmitters.filter(kind.isKind)
            : getStepSubmitterOptions({ submitters: getStepSubmitters(step).filter(kind.isKind) }, options.filter(kind.isKind)),
        );

        const showHireMonth = () => setHireMonthStepIndexes((indexes) => [...indexes, stepIndex]);
        const hideHireMonth = () => {
          setHireMonthStepIndexes((indexes) => indexes.filter((index) => index !== stepIndex));
          if (hireMonth) {
            onChangeStep({ [HIRE_MONTH_KEY]: null }, stepIndex);
          }
        };
        const changeHireMonth = (month) => {
          const kept = selectedSubmitters.filter((submitter) => isOfHireMonth(submitter, month));
          if (isFixed || kept.length === selectedSubmitters.length) {
            onChangeStep({ [HIRE_MONTH_KEY]: month }, stepIndex);
            return;
          }

          onChangeStepSubmitters(allowMultipleSubmitters ? kept : kept[0] ?? null, stepIndex, { [HIRE_MONTH_KEY]: month });
        };
        const stepOptions = STEP_OPTIONS.filter(
          ({ key }) =>
            (key === FORM_FOLDER_KEY && withFormFolders) ||
            (key === ONE_PER_GROUP_KEY && withOnePerGroup) ||
            (key === HIRE_DATE_FILTER_KEY && withHireDateOption),
        );

        const removeStepButton = isRemovable && (
          <button className="btn btn-icon btn-remove ml-4" onClick={() => onRemoveStep(step, stepIndex)}>
            <i className="icon-remove"></i>
          </button>
        );

        return (
          <div className="form-item mb-4" key={stepIndex}>
            <StepName
              name={stepName}
              defaultName={getStepName(null, stepIndex)}
              onRename={withStepNames ? (name) => onChangeStep({ [STEP_NAME_KEY]: name }, stepIndex) : null}
              hasControls={canFilter}
            >
              {canFilter && !isHireMonthShown && (
                <button
                  type="button"
                  className="btn btn-outline btn-outline-secondary strict-order-step-hire-button"
                  onClick={showHireMonth}
                >
                  <i className="icon-icon-calendar"></i>
                  Hire date
                </button>
              )}
            </StepName>
            {isHireMonthShown && <StepHireMonth hireMonth={hireMonth} onChange={changeHireMonth} onRemove={hideHireMonth} />}
            <div className={hasStepOptions ? `strict-order-step-row${splitByEntityType ? ' is-split' : ''}` : undefined}>
              <div className="strict-order-step-submitters">
                {splitByEntityType ? (
                  SUBMITTER_KINDS.map((kind, kindIndex) => {
                    const kindOptions = offeredOptions.filter(kind.isKind);
                    const selected = selectedByKind[kindIndex];
                    // A list keeps the other kind's submitters; a single one replaces them.
                    const handleChange = (value) =>
                      onChangeStepSubmitters(
                        allowMultipleSubmitters
                          ? selectedByKind.flatMap((kindSelected, index) => (index === kindIndex ? value ?? [] : kindSelected))
                          : value,
                        stepIndex,
                      );

                    return (
                      <div className="strict-order-step-picker" key={kind.key}>
                        <label className="form-label">{kind.label}</label>
                        <StepSubmittersPicker
                          allowMultipleSubmitters={allowMultipleSubmitters}
                          options={isFixed ? selected : kindOptions}
                          value={allowMultipleSubmitters ? selected : selected[0] ?? null}
                          isFixed={isFixed}
                          isDisabled={isFixed || (allowMultipleSubmitters && !kindOptions.length)}
                          isClearable={allowEmptySteps && !isFixed}
                          placeholder={kind.placeholder}
                          allName={kind.allName}
                          onChange={handleChange}
                          removeStepButton={kindIndex === 0 && removeStepButton}
                        />
                      </div>
                    );
                  })
                ) : (
                  <StepSubmittersPicker
                    allowMultipleSubmitters={allowMultipleSubmitters}
                    options={isFixed ? lockedSubmitters : offeredOptions}
                    value={allowMultipleSubmitters ? selectedSubmitters : selectedSubmitter}
                    isFixed={isFixed}
                    isDisabled={isFixed || (allowMultipleSubmitters && !offeredOptions.length)}
                    isClearable={allowEmptySteps && !isFixed}
                    placeholder="Choose one or several submitters"
                    onChange={(value) => onChangeStepSubmitters(value, stepIndex)}
                    removeStepButton={removeStepButton}
                  />
                )}

                {renderStepErrors && renderStepErrors(stepIndex)}
              </div>

              {hasStepOptions && (
                <div className="strict-order-step-options">
                  {stepOptions.map(({ key, label }) => (
                    <div className="form-control-checkbox" key={key}>
                      <input
                        type="checkbox"
                        checked={!!step?.[key]}
                        onChange={(e) => onChangeStep({ [key]: e.target.checked }, stepIndex)}
                      />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!hideNotifications && (
              <StrictOrderUserNotificationSettings
                stepIndex={stepIndex}
                notificationData={notificationData}
                onSetStepUserNotification={(value) => onChangeStep(value, stepIndex)}
                disabled={isNotificationReadOnly}
              />
            )}
          </div>
        );
      })}
      {!lockedFinalSubmitters && !isStepCountFixed && (
        <span className="add-facility cursor-pointer" onClick={onAddStep}>
          <i className="icon-plus-svgrepo-com mr-2"></i>Add order
        </span>
      )}
    </>
  );
};

export default memo(StrictOrderSteps);
